import { useState } from 'react'
import { isCurrentUserAdminSelector } from '@axilon-apps/domains-identity/state'
import {
  useAppSelector,
  useDeleteFeatureGroupMutation,
  useDeleteFlagOverrideMutation,
  useGetFlagSnapshotQuery,
  useListFeatureGroupsQuery,
  useListFlagOverridesQuery,
  usePutFeatureGroupMutation,
  usePutFlagOverrideMutation,
  type FlagOverride
} from '@axilon-apps/domains-ontology-ui-state'
import { FLAG_CATEGORIES, FLAG_DEFAULTS, FLAGS, type FlagKey } from '@atbash-labs/ontology-flags'

import { readMutationError } from '../../../components/explorer'
import { Badge } from '../../../components/ui/badge'
import { Button } from '../../../components/ui/button'
import { Card, CardContent } from '../../../components/ui/card'
import { Switch } from '../../../components/ui/switch'
import { useDebugMode } from '../../../lib/debug-mode'

const FLAG_KEYS = Object.keys(FLAG_DEFAULTS) as FlagKey[]

// One-line hint per registered key; adding a Register() entry fails typecheck
// until a hint is added here.
const FLAG_HINT = {
  [FLAGS.RELEASE_SIGHTING_PLANNER]:
    'Automatic observer sighting-plan generation and execution. Off until enabled for this organization.',
  [FLAGS.RELEASE_HISTORIAN_LATEST_TABLE]:
    'Live tag values and current-value tag statistics from the historian latest-value table. Enable after the historian-ingest backfill has finished.',
  [FLAGS.UI_INGEST_CANARY]:
    'Canary historian (Views Read API) ingest, binding, and read-through. Off until enabled for this organization.',
  [FLAGS.UI_SETTINGS_MOBILE_APP]:
    'Settings → General → Mobile app connection: pair the Prophet Android app by QR code and manage paired devices. Off until enabled for this organization.',
  [FLAGS.UI_INGEST_EXPERION]:
    'Honeywell Experion ERDB/EMDB ingest. Off until enabled for this organization.',
  [FLAGS.UI_INGEST_SRS]:
    'Safety Requirements Specification (.accdb) ingest. Off until enabled for this organization.',
  [FLAGS.UI_INGEST_SAFETY_MANAGER]:
    'Honeywell Safety Manager points-dump ingest. Off until enabled for this organization.',
  [FLAGS.UI_PLANT_DCS]: 'Plant View DCS tab. Off until enabled for this organization.',
  [FLAGS.UI_PLANT_NETWORK]: 'Plant View Network tab. Off until enabled for this organization.',
  [FLAGS.UI_PLANT_SAFETY]: 'Plant View Safety tab. Off until enabled for this organization.'
} as const satisfies Record<FlagKey, string>

function orgWideOverride(overrides: FlagOverride[], key: FlagKey): FlagOverride | undefined {
  return overrides.find((row) => row.key === key && row.deployment === '')
}

function scopedOverrideCount(overrides: FlagOverride[], key: FlagKey): number {
  return overrides.filter((row) => row.key === key && row.deployment !== '').length
}

/**
 * Org-wide flag overrides for global admins while debug mode is on.
 * Kill switches take effect on the server immediately; UI entitlements apply
 * on the next navigation because the session snapshot is pinned.
 */
export function FeatureFlagsCard() {
  const debugMode = useDebugMode()
  const isGlobalAdmin = useAppSelector(isCurrentUserAdminSelector)
  const canManage = debugMode && isGlobalAdmin

  const snapshotQuery = useGetFlagSnapshotQuery(undefined, { skip: !canManage })
  const overridesQuery = useListFlagOverridesQuery(undefined, { skip: !canManage })
  const groupsQuery = useListFeatureGroupsQuery(undefined, { skip: !canManage })
  const [putOverride] = usePutFlagOverrideMutation()
  const [deleteOverride] = useDeleteFlagOverrideMutation()
  const [putFeatureGroup] = usePutFeatureGroupMutation()
  const [deleteFeatureGroup] = useDeleteFeatureGroupMutation()

  const [pendingKey, setPendingKey] = useState<string | null>(null)
  const [saveError, setSaveError] = useState('')

  if (!canManage) return null

  const snapshot = snapshotQuery.data
  const overrides = overridesQuery.data ?? []
  const groups = groupsQuery.data ?? []
  const groupedKeys = new Set(groups.flatMap((group) => group.member_flag_keys))
  const standaloneKeys = FLAG_KEYS.filter((key) => !groupedKeys.has(key))
  const groupsLoading = snapshotQuery.isLoading || groupsQuery.isLoading
  const groupsLoadFailed = snapshotQuery.isError || groupsQuery.isError
  const flagsLoading = snapshotQuery.isLoading || overridesQuery.isLoading || groupsQuery.isLoading
  const flagsLoadFailed = snapshotQuery.isError || overridesQuery.isError || groupsQuery.isError

  const setEnabled = async (key: FlagKey, enabled: boolean) => {
    setSaveError('')
    setPendingKey(key)
    try {
      await putOverride({ key, enabled }).unwrap()
    } catch (error) {
      setSaveError(readMutationError(error))
    } finally {
      setPendingKey(null)
    }
  }

  const resetToDefault = async (key: FlagKey) => {
    setSaveError('')
    setPendingKey(key)
    try {
      await deleteOverride({ key }).unwrap()
    } catch (error) {
      setSaveError(readMutationError(error))
    } finally {
      setPendingKey(null)
    }
  }

  const setGroupEnabled = async (groupId: string, enabled: boolean) => {
    setSaveError('')
    setPendingKey(`group:${groupId}`)
    try {
      await putFeatureGroup({ groupId, enabled }).unwrap()
    } catch (error) {
      setSaveError(readMutationError(error))
    } finally {
      setPendingKey(null)
    }
  }

  const resetGroup = async (groupId: string) => {
    setSaveError('')
    setPendingKey(`group:${groupId}`)
    try {
      await deleteFeatureGroup(groupId).unwrap()
    } catch (error) {
      setSaveError(readMutationError(error))
    } finally {
      setPendingKey(null)
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {saveError ? (
        <div role="alert" className="rounded-md bg-st-down-bg px-3 py-2 text-xs text-st-down">
          {saveError}
        </div>
      ) : null}

      <Card className="gap-3 py-4">
        <CardContent className="flex flex-col gap-3">
          <div className="flex flex-col gap-0.5">
            <h3 className="text-sm font-semibold text-ink">Feature groups</h3>
            <p className="text-xs text-ink-3">
              Toggle related features together for this organization.
            </p>
          </div>

          {groupsLoading ? <p className="text-xs text-ink-3">Loading feature groups…</p> : null}

          {groupsLoadFailed ? (
            <p role="alert" className="text-xs text-st-down">
              Feature groups are unavailable. Check that this account has flags/manage.
            </p>
          ) : null}

          {snapshot && groups.length === 0 ? (
            <p className="text-xs text-ink-3">No feature groups are registered.</p>
          ) : null}

          {snapshot
            ? groups.map((group) => {
                const busy = pendingKey === `group:${group.id}`
                return (
                  <div
                    key={group.id}
                    className="flex items-start justify-between gap-3 border-t border-line pt-3"
                  >
                    <div className="flex min-w-0 flex-col gap-0.5">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="text-xs font-medium text-ink">{group.display_name}</span>
                        <Badge variant="outline">{group.overridden ? 'override' : 'default'}</Badge>
                      </div>
                      <span className="text-xs text-ink-3">
                        Toggles {group.member_flag_keys.join(', ')} together for this organization.
                      </span>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      {group.overridden ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          disabled={busy}
                          aria-label={`Reset ${group.display_name} feature group to default`}
                          onClick={() => void resetGroup(group.id)}
                        >
                          Reset
                        </Button>
                      ) : null}
                      <Switch
                        checked={group.enabled}
                        disabled={busy}
                        aria-label={`${group.display_name} feature group`}
                        onCheckedChange={(checked) =>
                          void setGroupEnabled(group.id, checked === true)
                        }
                      />
                    </div>
                  </div>
                )
              })
            : null}
        </CardContent>
      </Card>

      <Card className="gap-3 py-4">
        <CardContent className="flex flex-col gap-3">
          <div className="flex flex-col gap-0.5">
            <h3 className="text-sm font-semibold text-ink">Feature flags</h3>
            <p className="text-xs text-ink-3">
              Organization-wide overrides in Postgres. Kill switches apply on the server
              immediately. UI flags apply the next time you open a page.
            </p>
          </div>

          {flagsLoading ? <p className="text-xs text-ink-3">Loading feature flags…</p> : null}

          {flagsLoadFailed ? (
            <p role="alert" className="text-xs text-st-down">
              Feature flags are unavailable. Check that this account has flags/manage.
            </p>
          ) : null}

          {snapshot && standaloneKeys.length === 0 ? (
            <p className="text-xs text-ink-3">No standalone feature flags are registered.</p>
          ) : null}

          {snapshot
            ? standaloneKeys.map((key) => {
                const override = orgWideOverride(overrides, key)
                const scoped = scopedOverrideCount(overrides, key)
                const busy = pendingKey === key
                return (
                  <div
                    key={key}
                    className="flex items-start justify-between gap-3 border-t border-line pt-3"
                  >
                    <div className="flex min-w-0 flex-col gap-0.5">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="font-mono text-xs font-medium text-ink">{key}</span>
                        <Badge variant="secondary">{FLAG_CATEGORIES[key]}</Badge>
                        <Badge variant="outline">{override ? 'override' : 'default'}</Badge>
                      </div>
                      <span className="text-xs text-ink-3">{FLAG_HINT[key]}</span>
                      {scoped > 0 ? (
                        <span className="text-11 text-ink-3">
                          {scoped} deployment-specific override{scoped === 1 ? '' : 's'} still win
                          over this org-wide row.
                        </span>
                      ) : null}
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      {override ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          disabled={busy}
                          aria-label={`Reset ${key} to default`}
                          onClick={() => void resetToDefault(key)}
                        >
                          Reset
                        </Button>
                      ) : null}
                      <Switch
                        checked={snapshot.flags[key]}
                        disabled={busy || !snapshot}
                        aria-label={key}
                        onCheckedChange={(checked) => void setEnabled(key, checked === true)}
                      />
                    </div>
                  </div>
                )
              })
            : null}
        </CardContent>
      </Card>
    </div>
  )
}
